import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ComparisonPage.css';
import { useGetProductsQuery } from '@diditusa/shared/api/productsApi';
import { SecurityOption } from '@diditusa/shared/types/productTypes';

interface ComparisonPageProps {}

const ComparisonPage: React.FC<ComparisonPageProps> = () => {
  const { productId } = useParams<{ productId: string; productTitle: string }>();
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const { data: products } = useGetProductsQuery();
  const [sortedProducts, setSortedProducts] = useState<SecurityOption[]>([]);
  const [tooltip, setTooltip] = useState<{ row: number | null; col: number | null }>({
    row: null,
    col: null,
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (productId) {
      setSelectedPackageId(Number(productId));
    }
  }, [productId]);

  useEffect(() => {
    if (products && products.length > 0) {
      setSelectedPackageId(products[0].id);
      setSortedProducts([...products].sort((a, b) => a.pricing.unitAmount - b.pricing.unitAmount));
    }
  }, [products]);

  const features = [
    'Protection Agent',
    'Premium SUV Pick up (Chauffeur)',
    'Premium SUV Drop off (Chauffeur)',
    'Threat Mitigation Planning',
    'Dedicated Premium SUV & Chauffeur',
  ];

  const featureTooltips = [
    'A trained specialist ensuring your personal safety at all times.',
    'Private SUV transport with a professional chauffeur from your location.',
    'Private SUV drop-off service after your event or schedule.',
    'Strategic planning for identifying and neutralizing threats.',
    'A dedicated vehicle and chauffeur throughout the booking.',
  ];

  const featureDescriptions: { [feature: string]: string } = {
    'Protection Agent': '1 Trained professional providing personal protection.',
    'Premium SUV Pick up (Chauffeur)':
      'High-end chauffeured pickup service to desired destination.',
    'Premium SUV Drop off (Chauffeur)':
      'High-end chauffeured drop-off service from desired destination',
    'Threat Mitigation Planning': 'Custom risk assessment and threat response strategy.',
    'Dedicated Premium SUV & Chauffeur': 'Exclusive use of luxury vehicle and driver for duration.',
  };

  const featureMap: { [planId: number]: boolean[] } = {
    1: [true, true, true, true, true], // Prestige Tier
    2: [true, false, false, false, false], // Essential Tier
    3: [true, true, true, false, false], // Signature Tier
  };

  if (!products) {
    return <h1>Error, no products</h1>;
  }

  const selectedTier = (productTitle: string) => {
    navigate(`/tier-selected/${selectedPackageId}/${productTitle}`);
  };

  return (
    <div className='comparison-wrapper'>
      <div className='layout-container'>
        <div className='layout-content'>
          <div className='intro-section'>
            <div className='intro-text'>
              <p className='intro-title'>Compare. Choose. Protect.</p>
              <p className='intro-description'>
                Select the plan that aligns with your lifestyle,
                <br /> delivering peace of mind without compromise. Explore our tailored services
                side by side.
              </p>
            </div>
          </div>

          <div className='plan-cards'>
            {sortedProducts.map((plan) => (
              <div
                key={plan.id}
                className={`plan-card ${selectedPackageId === plan.id ? 'highlighted-plan' : ''}`}
                onClick={() => setSelectedPackageId(plan.id)}
              >
                <div className='plan-details'>
                  <h1 className='plan-title'>{plan.title}</h1>
                  <span className='plan-price-hours'>5 hours</span>
                  <p className='plan-price'>
                    <span className='plan-price-amount'>${plan.pricing.unitAmount / 100}</span>
                  </p>
                </div>
                <button className='get-started-button' onClick={() => selectedTier(plan.title)}>
                  <span>Reserve</span>
                </button>
              </div>
            ))}
          </div>

          <div className='comparison-table-wrapper'>
            {/* Desktop Table */}
            <table className='comparison-table'>
              <thead>
                <tr>
                  <th>What's Included</th>
                  {sortedProducts.map((plan) => (
                    <th key={plan.id}>{plan.title.split(' ')[0]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((feature, featureIdx) => (
                  <tr key={featureIdx}>
                    <td className='feature-with-tooltip'>
                      {feature}
                      <div className='tooltip-box'>{featureDescriptions[feature]}</div>
                    </td>
                    {sortedProducts.map((plan) => (
                      <td key={plan.id} className='text-center'>
                        <input
                          type='checkbox'
                          className='feature-checkbox'
                          readOnly
                          checked={featureMap[plan.id]?.[featureIdx] || false}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className='comparison-cards'>
              <div className='comparison-card'>
                <h3>{'Our Services'}</h3>
                <ul className='comparison-card-list'>
                  {features.map((feature, featureIdx) => (
                    <li key={featureIdx}>
                      <span className='feature-label'>{feature}</span>
                      <p className='tool'>{featureTooltips[featureIdx]}</p>
                    </li>
                  ))}
                </ul>
              </div>
              {sortedProducts.map((plan) => (
                <div className='comparison-card' key={plan.id}>
                  <h3>{plan.title}</h3>
                  <ul className='comparison-card-list'>
                    {features.map((feature, featureIdx) => (
                      <li key={featureIdx}>
                        <span>{feature}</span>
                        <input
                          type='checkbox'
                          className='feature-checkbox'
                          readOnly
                          checked={featureMap[plan.id]?.[featureIdx] || false}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonPage;
